import { useCallback, useMemo, useState } from "react";

// preset keys mirrored to the period selector component. backend doesn't care
// which preset triggered a range — we just send dateFrom/dateTo strings.
export const PRESETS = {
  LAST_30: "last_30",
  LAST_90: "last_90",
  YTD: "ytd",
  PREV_QUARTER: "prev_quarter",
  CUSTOM: "custom",
};

const DEFAULT_PRESET = PRESETS.LAST_90;

const isoDay = (d) => d.toISOString().slice(0, 10);

// returns [from, to] in YYYY-MM-DD for a preset, or null for custom.
export function rangeForPreset(preset, today = new Date()) {
  const to = new Date(today);

  if (preset === PRESETS.LAST_30) {
    const from = new Date(today);
    from.setDate(from.getDate() - 30);
    return [isoDay(from), isoDay(to)];
  }
  if (preset === PRESETS.LAST_90) {
    const from = new Date(today);
    from.setDate(from.getDate() - 90);
    return [isoDay(from), isoDay(to)];
  }
  if (preset === PRESETS.YTD) {
    const from = new Date(today.getFullYear(), 0, 1);
    return [isoDay(from), isoDay(to)];
  }
  if (preset === PRESETS.PREV_QUARTER) {
    // current calendar quarter index 0..3
    const q = Math.floor(today.getMonth() / 3);
    // previous quarter — wrap to prior year if currently Q1
    const prevQ = q === 0 ? 3 : q - 1;
    const prevQYear = q === 0 ? today.getFullYear() - 1 : today.getFullYear();
    const from = new Date(prevQYear, prevQ * 3, 1);
    const toEnd = new Date(prevQYear, prevQ * 3 + 3, 0); // last day of prev quarter
    return [isoDay(from), isoDay(toEnd)];
  }
  return null;
}

export default function useFlowAnalysisFilters() {
  const [preset, setPreset] = useState(DEFAULT_PRESET);

  // initial range matches DEFAULT_PRESET so the hook is consistent on first render
  const initial = rangeForPreset(DEFAULT_PRESET);
  const [dateFrom, setDateFrom] = useState(initial?.[0] || "");
  const [dateTo, setDateTo] = useState(initial?.[1] || "");

  const [materials, setMaterials] = useState([]);

  const applyPreset = useCallback((next) => {
    setPreset(next);
    if (next === PRESETS.CUSTOM) return; // dates are user-driven
    const range = rangeForPreset(next);
    if (range) {
      setDateFrom(range[0]);
      setDateTo(range[1]);
    }
  }, []);

  // when user manually edits a date, switch preset to custom so the chip selection
  // stays honest about what's actually applied
  const setCustomFrom = useCallback((v) => {
    setDateFrom(v);
    setPreset(PRESETS.CUSTOM);
  }, []);

  const setCustomTo = useCallback((v) => {
    setDateTo(v);
    setPreset(PRESETS.CUSTOM);
  }, []);

  return useMemo(
    () => ({
      preset,
      dateFrom,
      dateTo,
      materials,
      setMaterials,
      applyPreset,
      setCustomFrom,
      setCustomTo,
    }),
    [preset, dateFrom, dateTo, materials, applyPreset, setCustomFrom, setCustomTo]
  );
}
